import { useState, useEffect } from "react";
import { Trophy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

function FormBadge({ result }) {
  const colors = {
    W: "bg-volt/20 text-volt",
    D: "bg-txt-secondary/20 text-txt-secondary",
    L: "bg-danger/20 text-danger",
  };
  return (
    <span className={`text-[9px] font-bold px-1 py-0.5 rounded-sm ${colors[result] || ""}`}>
      {result}
    </span>
  );
}

export default function StandingsTable({ leagueCode, leagueName, onClose }) {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!leagueCode) return;
    setLoading(true);
    setError(null);
    api.getStandings(leagueCode)
      .then(data => {
        const raw = data.standings?.[0];
        const table = Array.isArray(raw?.[0]) ? raw[0] : (raw || []);
        setStandings(table);
      })
      .catch(() => setError("Erro ao carregar tabela"))
      .finally(() => setLoading(false));
  }, [leagueCode]);

  const getDescriptionColor = (desc) => {
    if (!desc) return "";
    if (desc.toLowerCase().includes("champions") || desc.toLowerCase().includes("promotion")) return "border-l-4 border-volt";
    if (desc.toLowerCase().includes("europa")) return "border-l-4 border-azure";
    if (desc.toLowerCase().includes("conference")) return "border-l-4 border-warning";
    if (desc.toLowerCase().includes("relegation")) return "border-l-4 border-danger";
    return "";
  };

  return (
    <div className="bg-surface border border-line rounded-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface-hover">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-volt" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest">
            {leagueName || "Tabela"}
          </h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-txt-secondary hover:text-white text-xs uppercase tracking-wider px-2 py-1 hover:bg-surface rounded-sm transition-colors">
            ✕
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-volt animate-spin" />
        </div>
      ) : error ? (
        <p className="text-danger text-sm text-center py-6">{error}</p>
      ) : (
        <div className="overflow-x-auto">
          {/* Table Header */}
          <div className="flex items-center px-3 py-2 border-b border-line/50 text-[10px] font-bold uppercase tracking-widest text-txt-secondary gap-2">
            <span className="w-6 text-center">{t('rank')}</span>
            <span className="w-40">{t('team')}</span>
            <span className="w-7 text-center">{t('played')}</span>
            <span className="w-7 text-center">{t('wins')}</span>
            <span className="w-7 text-center">{t('draws')}</span>
            <span className="w-7 text-center">{t('losses')}</span>
            <span className="w-8 text-center">{t('goalDiff')}</span>
            <span className="w-16 text-center hidden sm:block">{t('form')}</span>
            <span className="w-8 text-center font-black text-white">{t('points')}</span>
          </div>

          {/* Rows */}
          {standings.map((team) => (
            <div
              key={team.rank}
              className={`flex items-center px-3 py-2.5 border-b border-line/20 last:border-0 hover:bg-surface-hover transition-colors gap-2 ${getDescriptionColor(team.description)}`}
            >
              <span className="w-6 font-mono text-xs text-txt-secondary text-center">{team.rank}</span>
              <div className="w-40 flex items-center gap-2 min-w-0">
                <img
                  src={team.team?.logo}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <span className="text-sm font-medium truncate">{team.team?.name}</span>
              </div>
              <span className="w-7 font-mono text-xs text-center text-txt-secondary">{team.all?.played}</span>
              <span className="w-7 font-mono text-xs text-center text-volt">{team.all?.win}</span>
              <span className="w-7 font-mono text-xs text-center text-txt-secondary">{team.all?.draw}</span>
              <span className="w-7 font-mono text-xs text-center text-danger">{team.all?.lose}</span>
              <span className={`w-8 font-mono text-xs text-center ${team.goalsDiff > 0 ? "text-volt" : team.goalsDiff < 0 ? "text-danger" : "text-txt-secondary"}`}>
                {team.goalsDiff > 0 ? `+${team.goalsDiff}` : team.goalsDiff}
              </span>
              <div className="w-16 flex gap-0.5 justify-center hidden sm:flex">
                {team.form?.split("").map((r, i) => (
                  <FormBadge key={i} result={r} />
                ))}
              </div>
              <span className="w-8 font-mono text-sm font-black text-center text-white">{team.points}</span>
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center gap-4 px-3 py-2 border-t border-line/30 text-[10px] text-txt-secondary flex-wrap">
            <div className="flex items-center gap-1"><div className="w-3 h-3 border-l-4 border-volt" /><span>Champions</span></div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 border-l-4 border-azure" /><span>Europa</span></div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 border-l-4 border-warning" /><span>Conference</span></div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 border-l-4 border-danger" /><span>Descida</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
